import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { workPageMessages } from '../work/workPageMessages';
import { workProgressParams } from '../work/workProgressLink';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

export interface StudioMade {
  /** The name of the one asset ("Countdown"), or null for more than one. */
  name: string | null;
  count: number;
  /** The kit assets that "Make it" made: one asset has its row marked on the Work page. */
  uuids: string[];
}

interface StudioMadeBannerProps {
  made: StudioMade;
  catalogItemUuid: string;
  onClose: () => void;
}

/**
 * The short confirmation after "Make it" (design release-kit t2): "Making it: Countdown", and
 * "See the progress", a link to the work of the song on the Work page (owner, 2026-10-11; design
 * assets-rethink f4). The Studio keeps no strip of the work any more (t3). The link is the route
 * `promoteWork` with the song, `state=working` and, for one asset, `result=<uuid>`: its row has
 * the mark. In the native web view the page opens in the same web view.
 */
const StudioMadeBannerComponent: React.FC<StudioMadeBannerProps> = ({ made, catalogItemUuid, onClose }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openWork = useCallback(
    () =>
      router.push(
        SMLRoutes.promoteWork,
        workProgressParams(
          catalogItemUuid,
          'working',
          made.uuids.map((uuid) => ({ kind: 'asset', uuid }))
        )
      ),
    [catalogItemUuid, made.uuids, router]
  );
  const title =
    made.name === null
      ? intl.formatMessage(workPageMessages.madeKit, { count: made.count })
      : intl.formatMessage(workPageMessages.madeTitle, { name: made.name });

  return (
    <div className={styles.madeBanner} role="status" data-testid="studio-made-banner">
      <span className={styles.madeTick} aria-hidden="true">
        <FontAwesomeIcon icon={faCheck} />
      </span>
      <div className={styles.madeText}>
        <b>{title}</b>
        <small>{intl.formatMessage(workPageMessages.madeBody)}</small>
      </div>
      <SMLButton color="secondary" outline size="sm" omitCap onClick={openWork} testID="studio-made-see-progress">
        {intl.formatMessage(workPageMessages.madeLink)}
      </SMLButton>
      <SMLButton color="link" size="sm" omitCap onClick={onClose}>
        {intl.formatMessage(workPageMessages.madeClose)}
      </SMLButton>
    </div>
  );
};

export const StudioMadeBanner = TypedMemo(StudioMadeBannerComponent);
